import { useState, useEffect } from 'react'
import { DataQualityService } from '../../../../services/dataQuality'
import { TimeWindow } from '../../../../types/dataQuality'
import type { PipelineHealthData } from '../../../../types/dataQuality'
import './PipelineHealthTab.css'

interface PipelineHealthTabProps {
  timeWindow: TimeWindow
  onTimeWindowChange?: (timeWindow: TimeWindow) => void
}

interface PipelineStage {
  id: string
  name: string
  duration: string
  status: string
}

interface StatusSummary {
  type: string
  percentage: number
  label: string
  color: string
  textColor: string
  numberColor: string
}

interface ErrorType {
  id: string
  name: string
  count: number
  percentage: number
  color: string
}

const PipelineHealthTab = ({ timeWindow, onTimeWindowChange }: PipelineHealthTabProps) => {
  const [pipelineData, setPipelineData] = useState<PipelineHealthData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let isMounted = true

    const fetchPipelineHealth = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await DataQualityService.getPipelineHealth({
          time_window: timeWindow
        })

        // Only update state if component is still mounted
        if (isMounted) {
          setPipelineData(data)
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error fetching pipeline health:', err)
          setError('Failed to load pipeline health data')
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchPipelineHealth()

    // Cleanup function
    return () => {
      isMounted = false
    }
  }, [timeWindow])

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Loading pipeline health data...</p>
      </div>
    )
  }

  if (error || !pipelineData) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#ef4444' }}>
        <p>{error || 'No data available'}</p>
      </div>
    )
  }

  // Transform API data to match UI structure
  const pipelineSuccess = {
    title: 'End-to-End Pipeline Success',
    subtitle: pipelineData.end_to_end_pipeline_success.description,
    stages: pipelineData.end_to_end_pipeline_success.stages.map((stage) => ({
      id: stage.name.toLowerCase(),
      name: stage.name,
      duration: `${stage.duration_seconds.toFixed(1)}s`,
      status: stage.status
    })),
    statusSummary: [
      {
        type: 'successful',
        percentage: pipelineData.success_rate_breakdown.successful.percentage,
        label: pipelineData.success_rate_breakdown.successful.label,
        color: 'rgba(0, 201, 80, 0.1)',
        textColor: 'rgba(107, 114, 128, 1)',
        numberColor: 'rgba(0, 201, 80, 1)'
      },
      {
        type: 'partial',
        percentage: pipelineData.success_rate_breakdown.partial.percentage,
        label: pipelineData.success_rate_breakdown.partial.label,
        color: 'rgba(240, 177, 0, 0.1)',
        textColor: 'rgba(107, 114, 128, 1)',
        numberColor: 'rgba(240, 177, 0, 1)'
      },
      {
        type: 'failed',
        percentage: pipelineData.success_rate_breakdown.failed.percentage,
        label: pipelineData.success_rate_breakdown.failed.label,
        color: 'rgba(251, 44, 54, 0.1)',
        textColor: 'rgba(107, 114, 128, 1)',
        numberColor: 'rgba(251, 44, 54, 1)'
      }
    ]
  }

  // Color mapping for error types
  const errorTypeColors: Record<string, string> = {
    'Missing Values': '#2B7FFF99',
    'Format Issues': '#AD46FF99',
    'Range Violations': '#FF690099',
    'Duplicate Entries': '#F6339A99',
    'Conflicts': '#00BBA799'
  }

  const errorTracking = {
    title: 'Error Type Tracking',
    subtitle: pipelineData.error_type_tracking.description,
    totalErrors: pipelineData.error_type_tracking.total_errors,
    targetErrorRate: pipelineData.error_type_tracking.target_error_rate,
    errorTypes: pipelineData.error_type_tracking.categories.map((category) => ({
      id: category.name.toLowerCase().replace(/\s+/g, '-'),
      name: category.name,
      count: category.count,
      percentage: category.percentage,
      color: errorTypeColors[category.name] || category.color
    }))
  }

  const getStatusIcon = (status: string, stageId: string) => {
    if (stageId === 'validate') {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(239, 177, 0, 1)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="16" x2="12" y2="12"/>
          <line x1="12" y1="8" x2="12.01" y2="8"/>
        </svg>
      )
    }

    switch (status) {
      case 'success':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9 12l2 2 4-4"/>
          </svg>
        )
      case 'warning':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        )
      case 'error':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        )
      default:
        return null
    }
  }

  return (
    <>
      {/* Pipeline Header */}
      <div className="pipeline-header">
        <h2 className="pipeline-title">Pipeline Flow & Errors</h2>
        {onTimeWindowChange && (
          <select 
            className="pipeline-dropdown"
            value={timeWindow}
            onChange={(e) => onTimeWindowChange(e.target.value as TimeWindow)}
          >
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="quarter">Last 90 Days</option>
          </select>
        )}
      </div>

      {/* End-to-End Pipeline Success Section */}
      <div className="pipeline-success-section">
        <div className="section-header">
          <h3 className="section-title">{pipelineSuccess.title}</h3>
          <p className="section-subtitle">{pipelineSuccess.subtitle}</p>
        </div>

        {/* Pipeline Flow */}
        <div className="pipeline-flow">
          {pipelineSuccess.stages.map((stage: PipelineStage, index: number) => (
            <div key={stage.id} className="pipeline-stage">
              {/* Stage Circle */}
              <div className="stage-circle">
                {getStatusIcon(stage.status, stage.id)}
              </div>

              {/* Stage Info */}
              <div className="stage-info">
                <div className="stage-name">{stage.name}</div>
                <div className="stage-duration">{stage.duration}</div>
              </div>

              {/* Connection Line (except for last stage) */}
              {index < pipelineSuccess.stages.length - 1 && (
                <div className="stage-connection"></div>
              )}
            </div>
          ))}
        </div>

        {/* Status Summary */}
        <div className="status-summary">
          {pipelineSuccess.statusSummary.map((status: StatusSummary) => (
            <div
              key={status.type}
              className="status-box"
              style={{
                backgroundColor: status.color
              }}
            >
              <div
                className="status-percentage"
                style={{ color: status.numberColor }}
              >
                {status.percentage}%
              </div>
              <div
                className="status-label"
                style={{ color: status.textColor }}
              >
                {status.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Error Type Tracking Section */}
      <div className="error-tracking-section">
        <div className="error-tracking-header">
          <div className="section-header">
            <p className="section-title">{errorTracking.title}</p>
            <span className="section-subtitle">{errorTracking.subtitle}</span>
          </div>
          <div className="error-target">
            Target Error Rate: <span className="error-target-value">{errorTracking.targetErrorRate}</span>
          </div>
        </div>

        <div className="total-errors">
          Total Errors: <span className="total-errors-count">{errorTracking.totalErrors}</span>
        </div>

        {/* Error Types List */}
        <div className="error-types-list">
          {errorTracking.errorTypes.map((errorType: ErrorType) => (
            <div key={errorType.id} className="error-type-item">
              <div className="error-type-header">
                <div className="error-type-name">{errorType.name}</div>
                <div className="error-type-stats">{errorType.count} - {errorType.percentage}%</div>
              </div>
              <div className="error-progress-bar">
                <div
                  className="error-progress-fill"
                  style={{
                    width: `${errorType.percentage}%`,
                    backgroundColor: errorType.color
                  }}
                >
                  <span className="error-percentage-text">{errorType.percentage}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

export default PipelineHealthTab
